import { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { BellRing, ShieldAlert } from "lucide-react"
import { usePushNotifications } from "@/hooks/usePushNotifications"

export function NotificationPermissionDialog() {
  const { permission, isSubscribed, subscribe, isLoading, isSupported } = usePushNotifications()
  const [open, setOpen] = useState(false)

  useEffect(() => {
    // Show dialog if:
    // 1. Push notifications are supported
    // 2. Permission is not granted yet OR not subscribed
    // 3. User is logged in (check sessionStorage)
    const userInfo = sessionStorage.getItem('userInfo')
    if (!userInfo) return

    if (isSupported && (permission !== 'granted' || !isSubscribed)) {
      // Small delay to let the app render first
      const timer = setTimeout(() => setOpen(true), 2000)
      return () => clearTimeout(timer)
    }
  }, [permission, isSubscribed, isSupported])

  const handleEnable = async () => {
    const success = await subscribe()
    if (success) {
      setOpen(false)
    }
  }

  // Don't render if not supported or already subscribed with permission
  if (!isSupported || (permission === 'granted' && isSubscribed)) return null

  return (
    <Dialog open={open} onOpenChange={() => {/* prevent closing */}}>
      <DialogContent
        className="sm:max-w-md"
        showCloseButton={false}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex flex-col items-center text-center gap-3 mb-2">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#0d2846] to-[#e2af58] flex items-center justify-center">
              <BellRing className="h-8 w-8 text-white" />
            </div>
            <DialogTitle className="text-xl">Enable Notifications</DialogTitle>
            <DialogDescription className="text-sm">
              Notifications are required to receive important alerts about General Diary entries,
              case updates, and urgent action items.
            </DialogDescription>
          </div>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
            <ShieldAlert className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-sm text-amber-800 dark:text-amber-200">
              This is mandatory for all Pabandi Portal users to ensure you never miss critical updates about your cases.
            </p>
          </div>
        </div>
        <Button
          className="w-full bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846]/80 text-white mt-2"
          onClick={handleEnable}
          disabled={isLoading}
        >
          {isLoading ? (
            <span className="flex items-center gap-2">
              <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Enabling...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <BellRing className="h-4 w-4" />
              Enable Notifications
            </span>
          )}
        </Button>
        {permission === 'denied' && (
          <p className="text-xs text-center text-red-500 mt-2">
            Notifications are blocked in your browser. Please enable them in your browser settings and refresh the page.
          </p>
        )}
      </DialogContent>
    </Dialog>
  )
}
